import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Form,
  Icon,
  Image,
  List,
  Menu,
  Popup,
  Radio,
  Segment,
  Table,
  TextArea,
} from 'semantic-ui-react';
import PageContainer from '../common/PageContainer';
import * as selectors from '../../redux/selectors';
import { Link } from 'react-router-dom';
import * as actions from '../../redux/actions';
import { bindActionCreators } from 'redux';
import { TextField } from '../common/FormFields';
import { Formik } from 'formik';
import UserGroupDropDown from '../common/UserGroupDropDown';
import { userValidationSchema } from './admin.constants';
import AddUserToRoleDialog from './AddUserToRoleDialog';
import CloudinaryUploadWidget from '../showcase/CloudinaryUploadWidget';
import * as userApi from './../../api/userApi';

export const AdminUser = ({ user, match, setUser, updateUser, removeUserRole, resetPassword }) => {
  const id = match.params.id;
  const [rebelmouseSites, setRebelmouseSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setRebelmouseSites([]);
    setUser(id);
  }, [id]);

  useEffect(() => {
    if (user?.id !== id) return;
    if (!user?.email || rebelmouseSites?.length) return;
    userApi
      .getRebelMouseRoles(user?.email)
      .then(setRebelmouseSites)
      .catch(console.log)
      .finally(() => setLoading(false));
  }, [user, id]);

  const handleSubmit = (user, formik) => {
    updateUser(user);
    formik.setSubmitting(false);
  };

  const getBreadcrumb = () => (
    <Breadcrumb>
      <Breadcrumb.Section>Admin</Breadcrumb.Section>
      <Breadcrumb.Divider icon="right angle" />
      <Breadcrumb.Section>
        <Link to="/admin/user">Users</Link>
      </Breadcrumb.Section>
      <Breadcrumb.Divider icon="right angle" />
      <Breadcrumb.Section>Edit User</Breadcrumb.Section>
    </Breadcrumb>
  );

  const getMenu = formik => (
    <Menu borderless attached="top">
      <Menu.Item header>
        <Icon name="user circle outline" />
        {user.name}
      </Menu.Item>

      <Menu.Menu position="right">
        <Menu.Item>
          <AddUserToRoleDialog />
        </Menu.Item>
        <Menu.Item>
          <Button icon="redo" content="Reset password" onClick={() => resetPassword(user.email)} />
        </Menu.Item>
        <Menu.Item>
          <Button
            icon="save"
            positive
            content="Save"
            disabled={!formik.isValid || formik.isSubmitting}
            text="Save changes"
            onClick={() => formik.handleSubmit(formik)}
          />
        </Menu.Item>
      </Menu.Menu>
    </Menu>
  );

  const getUserForm = formik => (
    <Segment attached>
      <Form onSubmit={formik.handleSubmit}>
        <Image
          size="tiny"
          avatar
          src={formik.values.imageUrl || 'https://react.semantic-ui.com/images/wireframe/square-image.png'}
        />
        <br />
        <br />
        <CloudinaryUploadWidget formik={formik} fieldName="imageUrl" onSuccess={formik.handleSubmit} />

        <br />
        <br />

        <Form.Group widths="2">
          <TextField
            name="email"
            label="Email"
            formik={formik}
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            errorMessage={formik.errors.email}
          />
          <TextField
            name="name"
            label="Name"
            formik={formik}
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            errorMessage={formik.errors.name}
          />
          <UserGroupDropDown
            label="Organization"
            name="userGroupId"
            formik={formik}
            value={formik.values.userGroupId}
            error={formik.touched.userGroupId && Boolean(formik.errors.userGroupId)}
          />
          <Form.Field>
            <strong>RebelMouse Role</strong>
            <Dropdown
              fluid
              style={{ marginTop: 5 }}
              search
              selection
              value={formik.values.rebelmouseRole}
              clearable
              name="rebelmouseRole"
              onChange={(e, d) => formik.setFieldValue(d.name, d.value)}
              options={['Administrator', 'Guest Writer', 'Editor'].map(k => ({
                key: k,
                text: k,
                value: k,
              }))}
            />
          </Form.Field>
        </Form.Group>
        <Form.Field>
          <label>Is Disabled</label>
          <Radio
            toggle
            checked={formik.values.isDisabled}
            onChange={(e, d) => formik.setFieldValue('isDisabled', d.checked)}
          />
        </Form.Field>

        <Form.Field>
          <label>Bio</label>
          <TextArea value={formik.values.bio} onChange={(e, { value }) => formik.setFieldValue('bio', value)} />
        </Form.Field>
      </Form>
    </Segment>
  );

  const Roles = () => (
    <Card fluid>
      <Card.Content>
        <Card.Description>
          <Icon name="lock" />
          CMP Roles
        </Card.Description>
        <List selection verticalAlign="middle" divided>
          {user.roles.map(role => (
            <List.Item key={role.id}>
              <Popup trigger={<List.Content>{role.name}</List.Content>} on="click" position="bottom left" size="tiny">
                <Button
                  size="mini"
                  color="red"
                  icon="trash"
                  content="Remove Role"
                  fluid
                  onClick={() => removeUserRole(user, role)}
                />
              </Popup>
            </List.Item>
          ))}
        </List>
      </Card.Content>
    </Card>
  );

  const RebelMouseRoles = () => (
    <Segment basic loading={loading} padded={false} style={{ padding: 0 }}>
      <Card fluid>
        <Card.Content>
          <Card.Description>
            <Icon name="user" />
            RebelMouse User Profiles
          </Card.Description>

          <Table basic compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Site</Table.HeaderCell>
                <Table.HeaderCell>Slug</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rebelmouseSites.map(site => (
                <Table.Row key={site.id}>
                  <Table.Cell>{site.city}</Table.Cell>
                  <Table.Cell>
                    <a target="_blank" href={site.profile_url}>
                      {site.name}
                    </a>
                  </Table.Cell>
                  <Table.Cell>{site.is_active ? 'Active' : 'Inactive'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card.Content>
      </Card>
    </Segment>
  );

  const getUserPage = () => {
    return (
      <Formik
        enableReinitialize={true}
        initialValues={user}
        validationSchema={userValidationSchema}
        onSubmit={handleSubmit}
      >
        {formik => (
          <>
            {getBreadcrumb()}

            {getMenu(formik)}

            {getUserForm(formik)}

            <Roles />
            <RebelMouseRoles />
          </>
        )}
      </Formik>
    );
  };

  return <PageContainer>{user && user.id && getUserPage()}</PageContainer>;
};

const mapStateToProps = state => ({
  user: selectors.getCurrentAdminUser(state),
});

const mapDispatchToProps = dispatch => ({
  setUser: bindActionCreators(actions.admin.setUser, dispatch),
  updateUser: bindActionCreators(actions.admin.updateUser, dispatch),
  removeUserRole: bindActionCreators(actions.admin.removeUserRole, dispatch),
  resetPassword: bindActionCreators(actions.user.resetPassword, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminUser);
